import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  AnimatedItem,
  AnimatedWrapper,
} from '@/components/animation/AnimatedItem';
import { Button } from '@/components/buttons';
import { DepositPanel } from '@/components/general/Header/UserBlock';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { ModalSelectContainer } from '@/components/ModalSelect';
import { getProviderIcon } from '@/helpers/cdn';
import { useNavigation } from '@/hooks/navigation';
import JackpotBanner from '@/modules/jackpot/components/JackpotBanner';
import { useGetCommonStore } from '@/store';
import { Spinner } from '@/styles/common';
import { Addons } from '../../../components/Addons';
import { ToggleDemoSwitch } from '../../../components/ToggleDemoSwitch';
import type {
  ActionsPanelBasicProps,
  GameStateActions,
  MobileActionsPanelProps,
} from '../../../types';
import {
  ScActionsMobile,
  ScAddToFavoritesBtnContent,
  ScHomeLink,
  ScPanelFooter,
} from './styled';

export interface ActionsMobileProps
  extends MobileActionsPanelProps,
    ActionsPanelBasicProps,
    GameStateActions {
  onClose: () => void;
}

export const ActionsMobile: FC<ActionsMobileProps> = ({
  gameProvider,
  gameTitle,
  isDemo,
  isFavorite,
  handleFavorite,
  handleToggleDemo,
  onClose,
  isFullScreen,
  isDemoAvailable,
}) => {
  const { t } = useTranslation('common');
  const { generateLink } = useNavigation();

  const { blackColor, whiteColor, secondaryColor } = useTheme();
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const pendingFavoriteGames = useCommonStore.use.pendingFavoriteGames();

  const providerIcon = useMemo(
    () =>
      gameProvider ? (
        <ImageComponent
          alt="provider"
          height={22}
          src={getProviderIcon(gameProvider, 'color', 'small')}
          style={{ objectFit: 'contain' }}
          width={22}
        />
      ) : undefined,
    [gameProvider]
  );

  return (
    <ModalSelectContainer
      addons={
        <ScPanelFooter isAuthenticated={isAuthenticated}>
          {isAuthenticated && <DepositPanel mobile reducedSizeOnSmallScreen />}
          <Button small to={generateLink('/')}>
            <ScHomeLink>
              <IconComponent
                className="home-icon"
                name="homeIcon"
                size="15px"
              />
              <span>{t('gameCategories.lobby')}</span>
            </ScHomeLink>
          </Button>
        </ScPanelFooter>
      }
      bodyStyle={{
        padding: '0 0 16px',
      }}
      bottom={0}
      icon={providerIcon}
      innerAlignBottom
      onClose={onClose}
      title={gameTitle}
    >
      <ScActionsMobile>
        <AnimatedWrapper>
          <AnimatedItem>
            <div className="scroll-block__inner">
              <ToggleDemoSwitch
                handleToggleDemo={handleToggleDemo}
                horizontal
                isDemo={isDemo}
                isDemoAvailable={isDemoAvailable}
              />
              {isAuthenticated && (
                <Button onClick={handleFavorite} small white={isFavorite} wide>
                  <ScAddToFavoritesBtnContent isFavorite={isFavorite}>
                    <div className="icon__wrapper">
                      {pendingFavoriteGames ? (
                        <Spinner
                          background={isFavorite ? whiteColor : blackColor}
                          center
                          color={isFavorite ? secondaryColor : whiteColor}
                          size={20}
                        />
                      ) : (
                        <IconComponent
                          className={`${isFavorite ? 'heart-full' : ''}`}
                          name={isFavorite ? 'heartFullIcon' : 'heartIcon'}
                          size="20px"
                        />
                      )}
                    </div>
                    {isFavorite
                      ? `${t('game.buttons.removeFromFav')}`
                      : `${t('game.buttons.addToFav')}`}
                  </ScAddToFavoritesBtnContent>
                </Button>
              )}
              <div className="addons__wrapper">
                <Addons isDemo={isDemo} isFullScreen={isFullScreen} />
                <div className="jackpot-banner-wrapper">
                  <JackpotBanner />
                </div>
              </div>
            </div>
          </AnimatedItem>
        </AnimatedWrapper>
      </ScActionsMobile>
    </ModalSelectContainer>
  );
};
